// Website Brief — multi-step controller for the CF7 "Website Development Brief" form: keeps
// one CF7 form and shows one .kd-brief-step at a time. No-op without .kd-brief-form-wrap.

import { gsap } from 'gsap';

function isRequiredControl(el) {
  return el.classList.contains('wpcf7-validates-as-required');
}

function controlIsEmpty(el) {
  if (el.tagName === 'INPUT') {
    if (el.type === 'checkbox' || el.type === 'radio') return !el.checked;
    if (el.type === 'file') return el.files.length === 0;
    return el.value.trim() === '';
  }
  if (el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') {
    return el.value.trim() === '';
  }
  // Group wrapper (checkbox/radio/acceptance) — required class sits on the
  // wrapping <span>, not on the individual inputs.
  const inputs = el.querySelectorAll('input[type="checkbox"], input[type="radio"]');
  if (!inputs.length) return false;
  return !Array.from(inputs).some((input) => input.checked);
}

function setFieldError(controlEl, hasError) {
  const field = controlEl.closest('.kd-field');
  if (!field) return;

  field.classList.toggle('kd-field--error', hasError);

  if (controlEl.tagName === 'INPUT' || controlEl.tagName === 'TEXTAREA' || controlEl.tagName === 'SELECT') {
    controlEl.setAttribute('aria-invalid', hasError ? 'true' : 'false');
  }

  let message = field.querySelector('.kd-field__error');
  if (hasError) {
    if (!message) {
      message = document.createElement('p');
      message.className = 'kd-field__error';
      message.setAttribute('role', 'alert');
      message.textContent = 'This field is required.';
      field.appendChild(message);
    }
  } else if (message) {
    message.remove();
  }
}

function validateStep(stepEl) {
  const requiredControls = Array.from(
    stepEl.querySelectorAll('.wpcf7-validates-as-required')
  );

  let valid = true;
  let firstInvalid = null;

  requiredControls.forEach((el) => {
    const empty = controlIsEmpty(el);
    setFieldError(el, empty);
    if (empty) {
      valid = false;
      if (!firstInvalid) firstInvalid = el;
    }
  });

  return { valid, firstInvalid };
}

function toggleChipChecked(target) {
  if (!(target instanceof HTMLInputElement)) return;
  if (target.type !== 'checkbox' && target.type !== 'radio') return;

  const item = target.closest('.wpcf7-list-item');
  if (!item) return;

  if (target.type === 'radio' && target.name) {
    // Clear sibling chips in the same radio group before re-checking.
    const group = item.closest('.wpcf7-radio, .wpcf7-acceptance');
    if (group) {
      group
        .querySelectorAll('.wpcf7-list-item')
        .forEach((sibling) => sibling.classList.remove('is-checked'));
    }
  }

  item.classList.toggle('is-checked', target.checked);
}

function bindBriefForm(wrap) {
  const form = wrap.querySelector('form');
  if (!form) return;

  const steps = Array.from(form.querySelectorAll('.kd-brief-step'));
  if (!steps.length) return;

  const progress = form.querySelector('.kd-brief-progress');
  const ticks = progress
    ? Array.from(progress.querySelectorAll('.kd-brief-progress__tick'))
    : [];
  const fill = progress ? progress.querySelector('.kd-brief-progress__fill') : null;
  const countCurrentEl = progress
    ? progress.querySelector('.kd-brief-progress__count-current')
    : null;
  const eyebrowEl = progress ? progress.querySelector('.kd-brief-progress__eyebrow') : null;
  const total = steps.length;
  let current = 1;
  let firstRender = true;

  function showStep(stepNumber) {
    current = Math.min(Math.max(stepNumber, 1), total);

    steps.forEach((stepEl, idx) => {
      const isTarget = idx + 1 === current;
      stepEl.hidden = !isTarget;
      stepEl.setAttribute('aria-hidden', isTarget ? 'false' : 'true');
    });

    if (progress) progress.setAttribute('aria-valuenow', String(current));
    if (fill) fill.style.width = `${(current / total) * 100}%`;
    if (countCurrentEl) countCurrentEl.textContent = String(current).padStart(2, '0');

    let activeLabel = '';
    ticks.forEach((tick) => {
      const stepNum = Number(tick.dataset.step);
      const isComplete = stepNum < current;
      tick.classList.toggle('is-active', stepNum === current);
      tick.classList.toggle('is-complete', isComplete);
      tick.disabled = !isComplete;
      if (stepNum === current) activeLabel = tick.dataset.label || '';
    });

    if (eyebrowEl) eyebrowEl.textContent = activeLabel;

    const currentStepEl = steps[current - 1];
    const heading = currentStepEl.querySelector('.kd-brief-step__title');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
    wrap.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // A quick fade/rise on the incoming step — skipped on first render so
    // the page doesn't visibly "pop in" on load.
    if (firstRender) {
      firstRender = false;
    } else {
      gsap.fromTo(
        currentStepEl,
        { autoAlpha: 0, y: 18 },
        { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.out' }
      );
    }
  }

  steps.forEach((stepEl, idx) => {
    const nextBtn = stepEl.querySelector('.kd-brief-next');
    const prevBtn = stepEl.querySelector('.kd-brief-prev');

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        const { valid, firstInvalid } = validateStep(stepEl);
        if (!valid) {
          if (firstInvalid && typeof firstInvalid.focus === 'function') {
            firstInvalid.focus();
          }
          return;
        }
        if (idx + 1 < total) showStep(idx + 2);
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        if (idx > 0) showStep(idx);
      });
    }
  });

  // Jump back to any already-completed step from the progress track. Ticks are real <button>s,
  // disabled via showStep() until their step is complete, so no extra click-guard is needed here.
  ticks.forEach((tick) => {
    tick.addEventListener('click', () => {
      const stepNum = Number(tick.dataset.step);
      if (stepNum < current) showStep(stepNum);
    });
  });

  // Clear a field's error the moment it's fixed, and keep the "chip" look
  // for checkboxes/radios in sync with their checked state.
  form.addEventListener('input', (event) => {
    if (isRequiredControl(event.target)) {
      setFieldError(event.target, controlIsEmpty(event.target));
    }
  });

  form.addEventListener('change', (event) => {
    toggleChipChecked(event.target);

    const group = event.target.closest('.wpcf7-form-control');
    if (group && isRequiredControl(group)) {
      setFieldError(group, controlIsEmpty(group));
    }
  });

  // After a successful AJAX submit, reset to step 1 so the form is ready
  // to be filled out again (e.g. an admin testing it, or a shared kiosk).
  form.addEventListener('wpcf7mailsent', () => showStep(1));

  showStep(1);
}

export default function initWebsiteBrief(root = document) {
  root.querySelectorAll('.kd-brief-form-wrap').forEach((wrap) => {
    if (wrap._kdBriefBound) return;
    wrap._kdBriefBound = true;
    bindBriefForm(wrap);
  });
}
